import React, { useState } from "react";

import { GripVertical, Phone, Mail } from "lucide-react";

import { cn } from "../../lib/utils";

/**
 * Quadro de leads por etapa, com arrastar e soltar.
 *
 * Drag and drop nativo do HTML5 em vez de biblioteca: são cartões simples numa
 * grade de colunas fixas, e trazer uma dependência de 40 kB para isso não se
 * paga. O toque no celular não tem arrastar nativo — por isso cada cartão
 * também traz o seletor de etapa, que é o caminho de quem usa no telefone.
 */
const LeadsKanban = ({ colunas, leads, onMover, corDaEtapa }) => {
	const [arrastando, setArrastando] = useState(null);
	const [sobre, setSobre] = useState(null);

	const porEtapa = (valor) => leads.filter((l) => l.status === valor);

	const soltar = (etapa) => {
		setSobre(null);

		if (!arrastando || arrastando.status === etapa) {
			setArrastando(null);
			return;
		}

		onMover(arrastando, etapa);
		setArrastando(null);
	};

	return (
		<div className="flex gap-3 overflow-x-auto pb-2">
			{colunas.map((coluna) => {
				const daColuna = porEtapa(coluna.slug);

				return (
					<div
						key={coluna.slug}
						onDragOver={(e) => {
							// Sem o preventDefault o navegador recusa a soltura e o
							// cartão volta para a origem com a animação de "não pode".
							e.preventDefault();
							setSobre(coluna.slug);
						}}
						onDragLeave={() => setSobre((a) => (a === coluna.slug ? null : a))}
						onDrop={() => soltar(coluna.slug)}
						className={cn(
							"flex w-72 shrink-0 flex-col rounded-xl border bg-card transition-colors",
							sobre === coluna.slug && "border-primary bg-primary/5"
						)}
					>
						<div className="flex items-center justify-between border-b px-3 py-2.5">
							<span className="flex min-w-0 items-center gap-2 text-sm font-semibold">
								{/* A cor é da etapa cadastrada: é o que permite bater o
								    olho e achar a coluna no meio de oito. */}
								<span
									className="h-2.5 w-2.5 shrink-0 rounded-full"
									style={{ backgroundColor: coluna.color || "#64748b" }}
								/>
								<span className="truncate">{coluna.name}</span>
							</span>
							<span className="rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums text-muted-foreground">
								{daColuna.length}
							</span>
						</div>

						<div className="flex flex-1 flex-col gap-2 p-2">
							{daColuna.map((lead) => (
								<div
									key={lead.id}
									draggable
									onDragStart={() => setArrastando(lead)}
									onDragEnd={() => setArrastando(null)}
									className={cn(
										"group cursor-grab rounded-lg border bg-background p-2.5 shadow-xs transition-opacity active:cursor-grabbing",
										arrastando?.id === lead.id && "opacity-40"
									)}
								>
									<div className="flex items-start gap-1.5">
										<GripVertical className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
										<div className="min-w-0 flex-1">
											<p className="truncate text-sm font-medium">
												{lead.name}
											</p>
											{lead.phone && (
												<p className="mt-0.5 flex items-center gap-1 truncate text-xs text-muted-foreground">
													<Phone className="h-3 w-3 shrink-0" />
													{lead.phone}
												</p>
											)}
											{lead.email && (
												<p className="mt-0.5 flex items-center gap-1 truncate text-xs text-muted-foreground">
													<Mail className="h-3 w-3 shrink-0" />
													{lead.email}
												</p>
											)}

											{/* Caminho de quem usa no celular: lá não há arrastar. */}
											<select
												value={lead.status}
												onChange={(e) => onMover(lead, e.target.value)}
												className="mt-2 w-full rounded border bg-transparent px-1.5 py-1 text-xs text-muted-foreground"
												aria-label={`Mover ${lead.name} de etapa`}
											>
												{colunas.map((c) => (
													<option key={c.slug} value={c.slug}>
														{c.name}
													</option>
												))}
											</select>
										</div>
									</div>
								</div>
							))}

							{daColuna.length === 0 && (
								<p className="px-1 py-6 text-center text-xs text-muted-foreground">
									Arraste um lead para cá
								</p>
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
};

export default LeadsKanban;
